import React, { useRef, useState } from 'react';
import axios from 'axios';
import { X, Mic, Square, Image as ImageIcon, Video, Loader2, Type, Send, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import { myiaTexts } from '../../utils/myiaTranslations';
import { mediaStudioTexts } from './mediaStudioTexts';
import VideoEditorModal from './VideoEditorModal';
import { Film, Clapperboard } from 'lucide-react';

/**
 * MediaGeneratorModal — Modal that lets the user generate an IMAGE (Nano Banana)
 * or a short VIDEO (Gemini Omni Flash) from either:
 *   - a voice prompt (recorded, transcribed via Whisper)
 *   - a written prompt (optionally with up to 2 reference images)
 */
export default function MediaGeneratorModal({ open, onClose, onGenerated }) {
  const { language } = useLanguage();
  const M = myiaTexts(language);
  const S = mediaStudioTexts(language);
  const [showEditor, setShowEditor] = useState(false);
  const [videoRef, setVideoRef] = useState(null); // data URL of a source clip (<= 10 s)
  const [aspect, setAspect] = useState('9:16');
  const [jobStatus, setJobStatus] = useState('');
  const videoFileRef = useRef(null);
  const [mode, setMode] = useState('write'); // 'write' | 'voice'
  const [media, setMedia] = useState('image'); // 'image' | 'video'
  const [text, setText] = useState('');
  const [duration, setDuration] = useState(8);
  const [refs, setRefs] = useState([]); // [data-url,...] max 3 (image) / 5 (video)
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  if (!open) return null;
  const maxRefs = media === 'image' ? 3 : 5;

  const addVideoRef = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error('> 25 Mo'); e.target.value = ''; return; }
    const r = new FileReader();
    r.onload = () => setVideoRef(r.result);
    r.readAsDataURL(f);
    e.target.value = '';
  };

  const addRef = (e) => {
    const files = Array.from(e.target.files || []).slice(0, maxRefs - refs.length);
    files.forEach(f => {
      const r = new FileReader();
      r.onload = () => setRefs(prev => [...prev, r.result].slice(0, maxRefs));
      r.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const removeRef = (i) => setRefs(prev => prev.filter((_, idx) => idx !== i));

  const startVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'prompt.webm');
          const r = await axios.post('/api/film-ia/speech-to-text', fd);
          setText(r.data?.text || '');
        } catch {
          toast.error(M.mgTranscribeKo);
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch { toast.error(M.vcMicKo); }
  };

  const stopVoice = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const generate = async () => {
    if (!text.trim()) { toast.error(M.mgNoPrompt); return; }
    setGenerating(true);
    try {
      const r = await axios.post('/api/myia-voice/generate-media', {
        prompt: text,
        media,
        duration_sec: media === 'video' ? Number(duration) : undefined,
        image_refs: refs.length ? refs : undefined,
        video_ref: media === 'video' && videoRef ? videoRef : undefined,
        aspect_ratio: media === 'video' ? aspect : undefined,
      }, { timeout: 300000 });
      let videoUrl = r.data?.video_url;
      if (media === 'video' && r.data?.job_id) {
        setJobStatus(S.queued);
        for (let i = 0; i < 120; i++) {
          await new Promise(res => setTimeout(res, 5000));
          const j = await axios.get(`/api/myia-voice/generate-media/jobs/${r.data.job_id}`);
          if (j.data.status === 'completed') { videoUrl = j.data.video_url; break; }
          if (j.data.status === 'failed') throw new Error(j.data.error || S.videoFailed);
        }
        if (!videoUrl) throw new Error(S.videoFailed);
        toast.success(S.videoDone);
      }
      onGenerated && onGenerated({
        prompt: text,
        media,
        image_b64: r.data?.image_b64,
        video_url: videoUrl,
        format: r.data?.format,
      });
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || M.mgKo);
    } finally {
      setGenerating(false);
      setJobStatus('');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center" data-testid="media-generator-modal">
      <div className="w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl bg-gradient-to-b from-gray-900 to-black border border-fuchsia-500/30 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-black/90 backdrop-blur border-b border-white/10 p-3 flex items-center justify-between z-10">
          <h3 className="font-bold bg-gradient-to-r from-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
            {media === 'image' ? M.mgTitleImage : M.mgTitleVideo}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-full bg-white/10 hover:bg-white/20" data-testid="media-generator-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Video editor (full width, above image / video) */}
          <button
            onClick={() => setShowEditor(true)}
            className="w-full py-2.5 rounded-full text-xs font-semibold flex items-center justify-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white"
            data-testid="media-open-video-editor"
          >
            <Clapperboard className="w-4 h-4" /> {S.editorBtn}
          </button>
          <VideoEditorModal open={showEditor} onClose={() => setShowEditor(false)} />

          {/* Media type selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMedia('image')}
              className={`py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${media === 'image' ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500' : 'bg-white/10'}`}
              data-testid="media-type-image"
            >
              <ImageIcon className="w-3.5 h-3.5" /> {M.mgTypeImage}
            </button>
            <button
              onClick={() => setMedia('video')}
              className={`py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${media === 'video' ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500' : 'bg-white/10'}`}
              data-testid="media-type-video"
            >
              <Video className="w-3.5 h-3.5" /> {M.mgTypeVideo}
            </button>
          </div>

          {/* Input mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode('write')}
              className={`py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${mode === 'write' ? 'bg-white/20' : 'bg-white/5 text-white/60'}`}
              data-testid="media-mode-write"
            >
              <Type className="w-3.5 h-3.5" /> {M.mgWrite}
            </button>
            <button
              onClick={() => setMode('voice')}
              className={`py-2 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${mode === 'voice' ? 'bg-white/20' : 'bg-white/5 text-white/60'}`}
              data-testid="media-mode-voice"
            >
              <Mic className="w-3.5 h-3.5" /> {M.mgSpeak}
            </button>
          </div>

          {/* Prompt input */}
          {mode === 'voice' ? (
            <div className="flex items-center gap-2">
              <button
                onClick={recording ? stopVoice : startVoice}
                className={`p-3 rounded-full ${recording ? 'bg-red-500 animate-pulse' : 'bg-gradient-to-r from-fuchsia-500 to-pink-500'}`}
                data-testid="media-voice-mic"
              >
                {recording ? <Square className="w-4 h-4" fill="white" /> : <Mic className="w-4 h-4" />}
              </button>
              <div className="flex-1 text-xs text-white/70">
                {recording ? M.mgRecording : transcribing ? M.mgTranscribing : text || M.mgHint}
              </div>
            </div>
          ) : (
            <textarea
              value={text} onChange={e => setText(e.target.value)} rows={3}
              placeholder={media === 'image' ? M.mgPhImg : M.mgPhVid}
              className="w-full bg-black/40 border border-white/10 rounded-xl p-2.5 text-xs resize-none focus:outline-none focus:border-fuchsia-400"
              data-testid="media-prompt-textarea"
            />
          )}

          {/* Reference images: 3 (image) / 5 (video) */}
          {(
            <div>
              <div className="text-[11px] text-white/60 mb-1.5">{media === 'image' ? M.mgRefs : S.videoRefs}</div>
              <div className="flex gap-2">
                {refs.map((r, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-white/20">
                    <img src={r} alt="ref" className="w-full h-full object-cover" />
                    <button onClick={() => removeRef(i)} className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/70" data-testid={`media-ref-remove-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {refs.length < maxRefs && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="w-16 h-16 rounded-xl border border-dashed border-white/30 flex items-center justify-center hover:border-fuchsia-400"
                    data-testid="media-ref-add"
                  >
                    <Upload className="w-4 h-4 text-white/50" />
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" multiple onChange={addRef} className="hidden" />
              </div>
            </div>
          )}

          {/* Source video (recast) — video only */}
          {media === 'video' && (
            <div>
              <div className="text-[11px] text-white/60 mb-1.5">{S.sourceVideo}</div>
              {videoRef ? (
                <div className="flex items-center gap-2 text-xs bg-white/5 rounded-xl p-2" data-testid="media-video-ref">
                  <Film className="w-4 h-4 text-fuchsia-400" />
                  <video src={videoRef} className="h-14 rounded-lg" muted playsInline />
                  <button onClick={() => setVideoRef(null)} className="ml-auto p-1 rounded-full bg-black/60" data-testid="media-video-ref-remove"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <button onClick={() => videoFileRef.current?.click()} className="w-full py-2 rounded-xl border border-dashed border-white/30 text-[11px] flex items-center justify-center gap-1 hover:border-fuchsia-400" data-testid="media-video-ref-add">
                  <Upload className="w-3.5 h-3.5 text-white/50" /> {S.sourceVideo}
                </button>
              )}
              <input ref={videoFileRef} type="file" accept="video/*" onChange={addVideoRef} className="hidden" />
              <p className="text-[10px] text-white/40 mt-1">{S.sourceHint}</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {[['9:16', S.portrait], ['16:9', S.landscape]].map(([v, lab]) => (
                  <button key={v} onClick={() => setAspect(v)} className={`py-1.5 rounded-full text-[11px] ${aspect === v ? 'bg-fuchsia-500/40 border border-fuchsia-400' : 'bg-white/5'}`} data-testid={`media-aspect-${v.replace(':', '-')}`}>{lab}</button>
                ))}
              </div>
            </div>
          )}

          {/* Duration slider (video only) */}
          {media === 'video' && (
            <div>
              <div className="flex items-center justify-between text-[11px] text-white/60 mb-1">
                <span>{M.mgDuration}</span>
                <span className="font-semibold text-fuchsia-300">{duration}s</span>
              </div>
              <input
                type="range" min={3} max={10} step={1}
                value={duration} onChange={e => setDuration(Number(e.target.value))}
                className="w-full accent-fuchsia-500"
                data-testid="media-duration-slider"
              />
            </div>
          )}

          <button
            onClick={generate}
            disabled={generating || !text.trim()}
            className="w-full py-3 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 font-semibold text-xs disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="media-generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {generating ? (jobStatus || M.mgRunning) : (media === 'image' ? M.mgRunImage : M.mgRunVideo)}
          </button>
        </div>
      </div>
    </div>
  );
}
